import * as Yup from "yup"
import i18next from "i18next"
import {
  getAlfaNumericChar,
  getArabicDescMessage,
  getArabicLinkMessage,
  getDialCodeStart,
  getDisplayNumMessage,
  getEmailMessage,
  getExpiredDateMsg,
  getFieldOnlyLetters,
  getFieldRequiredMessage,
  getStartEndTimeDifferentMsg,
  getGreaterOrEqual0,
  getInvalidFormat,
  getMaxExact1,
  getMaxMustGreaterMin,
  getMin6PasswordMessage,
  getMinExact1,
  getMinimumNegMessage,
  getMinOneCustomer,
  getMinOneItemGroup,
  getMinOneLocation,
  getMinOneSetting,
  getNoSpaceMessage,
  getOnlyNumAndChar,
  getOnlyNumMessage,
  getPasswordComplexityMessage,
  getPasswordMatchMessage,
  getQtyNum,
  getQtyPositive,
  getValidArabicMessage,
  getValidEmail,
  getValidNumMessage,
  getValue0To100,
  getValueBt1To10,
  getValueBt1To1000,
  getValueBt3To10,
} from "./validationMessages"
import { bannerHeight, bannerWidth } from "components/Common/ImageSize"

// const arabicNameOrNumberPattern = /^[\u0600-\u06FF0-9\s]+$/
const arabicNameOrNumberPattern = /^[\u0600-\u06FF0-9\s\p{P}\p{S}]+$/u

const createSchemaShape = shape => {
  return Yup.object().shape(shape)
}

// Min 8 chars, at least one uppercase, one lowercase, one number, one special char.
const PASSWORD_POLICY_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/

const passwordPolicySchema = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .min(8, getMin6PasswordMessage())
    .matches(PASSWORD_POLICY_REGEX, getPasswordComplexityMessage())

export const addLocationSchema = mode => {
  let shape = {
    locationNameArabic: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    addressAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    locationNameEnglish: Yup.string().required(getFieldRequiredMessage()),
    // country: Yup.string().required(getFieldRequiredMessage()),
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        getValidEmail()
      ),
    latitude: Yup.string().required(getFieldRequiredMessage()),
    longitude: Yup.string().required(getFieldRequiredMessage()),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageURL: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
    }),
    // imageFile: Yup.string().when("imageType", {
    //   is: val => val === "UPLOAD",
    //   then: () => Yup.string().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),
    countryId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),

    regionId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),

    cityId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
      branchId: Yup.string()
        .matches(/^\S+$/, getNoSpaceMessage())
        .required(getFieldRequiredMessage()),
    }
  }

  return createSchemaShape(shape)
}

export const addRoleSchemas = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
  }

  return createSchemaShape(shape)
}

export const addUserSchemas = () => {
  const shape = {
    firstName: Yup.string().required(getFieldRequiredMessage()),
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        getValidEmail()
      )
      .required(getEmailMessage()),
    password: passwordPolicySchema(),
    lastName: Yup.string().required(getFieldRequiredMessage()),
    // username: Yup.string().required(getFieldRequiredMessage()),
    // mobile: Yup.string().required(getFieldRequiredMessage()),
    roles: Yup.object().required(getFieldRequiredMessage()),
    locations: Yup.array().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}
export const editUserSchemas = () => {
  const shape = {
    firstName: Yup.string().required(getFieldRequiredMessage()),
    lastName: Yup.string().required(getFieldRequiredMessage()),
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        getValidEmail()
      )
      .required(getEmailMessage()),
    roles: Yup.object().required(getFieldRequiredMessage()),
    // mobile: Yup.string().required(getFieldRequiredMessage()),
    locations: Yup.array().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const resetPasswordSchema = () => {
  const shape = {
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        getValidEmail()
      )
      .required(getEmailMessage()),
  }

  return createSchemaShape(shape)
}
export const verifyPasswordSchema = () => {
  const shape = {
    newPassword: passwordPolicySchema(),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const userResetPasswordSchema = () => {
  const shape = {
    password: Yup.string().required(getFieldRequiredMessage()),
    newPassword: passwordPolicySchema(),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const resetPasswordSchemaForUser = () => {
  const shape = {
    newPassword: passwordPolicySchema(),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const updatePasswordSchema = () => {
  const shape = {
    newPassword: passwordPolicySchema(),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const verifyOTPSchema = () => {
  const shape = {
    otp: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addBannerSetSchema = () => {
  const shape = {
    name: Yup.string()
      .matches(/^[a-zA-Z0-9\s]*$/, getAlfaNumericChar())
      .required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[\u0600-\u06FF0-9\s]+$/, getValidArabicMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addSlideSchema = () => {
  let shape = {
    // imageType: Yup.string().required(getFieldRequiredMessage()),
    // imageURL: Yup.string().when("imageType", {
    //   is: "URL",
    //   then: Yup.string().url("Invalid URL").required(getFieldRequiredMessage()),
    // }),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageFile: Yup.string().when("imageType", {
      is: val => val === "UPLOAD",
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    imageURL: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .test(
            "image-dimensions",
            `Image dimensions must be ${bannerWidth}x${bannerHeight} pixels.`,
            function (value) {
              if (!value) return true // Let required validation handle empty values

              return new Promise(resolve => {
                const img = new window.Image()
                img.onload = () => {
                  resolve(
                    img.width === bannerWidth && img.height === bannerHeight
                  )
                }
                img.onerror = () => {
                  resolve(false)
                }
                img.src = value
              })
            }
          ),
    }),
    imageUrlAr: Yup.string().when("imageUrlTypeAr", {
      is: val => val === "URL",
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .test(
            "image-dimensions-ar",
            `Image dimensions must be ${bannerWidth}x${bannerHeight} pixels.`,
            function (value) {
              if (!value) return true // Let required validation handle empty values

              return new Promise(resolve => {
                const img = new window.Image()
                img.onload = () => {
                  resolve(
                    img.width === bannerWidth && img.height === bannerHeight
                  )
                }
                img.onerror = () => {
                  resolve(false)
                }
                img.src = value
              })
            }
          ),
    }),
    titleAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getArabicDescMessage()
    ),
    linkLabelAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getArabicLinkMessage()
    ),
    displayOrder: Yup.number()
      .typeError(getDisplayNumMessage())
      .required(getFieldRequiredMessage())
      .min(0, getMinimumNegMessage()),
    // imageFile: Yup.mixed().when("imageType", {
    //   is: "UPLOAD",
    //   then: Yup.mixed().required("Image file is required"),
    // }),
  }

  return createSchemaShape(shape)
}

export const addCategoriesSchemas = mode => {
  let shape = {
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    nameEn: Yup.string().required(getFieldRequiredMessage()),
    displayOrder: Yup.number()
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
    }),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addItemSchemas = mode => {
  let shape = {
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    nameEn: Yup.string().required(getFieldRequiredMessage()),
    displayOrder: Yup.number()
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .required(getFieldRequiredMessage()),
    itemPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    originalPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    personCountPerServing: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+$/, getOnlyNumMessage()),
    caloriesForCalculations: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+$/, getOnlyNumMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    category: Yup.string().required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    announcementAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    availabilityStartTime: Yup.string().when("availabilityHasSpecificTime", {
      is: val => val === true,
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    availabilityEndTime: Yup.string().when("availabilityHasSpecificTime", {
      is: val => val === true,

      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .test(
            "times-not-same",
            getStartEndTimeDifferentMsg(),
            function (value) {
              const { availabilityStartTime } = this.parent
              return (
                !value ||
                !availabilityStartTime ||
                value !== availabilityStartTime
              )
            }
          ),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "add" || mode === "allItemsAdd") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addComboSchemas = mode => {
  let shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    displayOrder: Yup.number()
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .required(getFieldRequiredMessage()),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),

    discount: Yup.string().when("isDiscountAllowed", {
      is: val => val === true,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^\d+$/, getValidNumMessage())
          .test("min-max-values", getValue0To100(), value => {
            const number = parseInt(value, 10)
            return number >= 0 && number <= 100
          }),
      otherwise: schema => schema.notRequired(),
    }),

    originalPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    personCountPerServing: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+$/, getOnlyNumMessage()),
    // calorieCount: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .matches(/^\d+$/, getOnlyNumMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    startDateAndTime: Yup.string().when("hasSpecificDateAndTime", {
      is: val => val === true,
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    endDateAndTime: Yup.string().when("hasSpecificDateAndTime", {
      is: val => val === true,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .test("is-greater", getExpiredDateMsg(), function (value) {
            const { startDateAndTime } = this.parent
            return new Date(value) > new Date(startDateAndTime)
          }),
      otherwise: schema => schema.notRequired(),
    }),

    categoryId: Yup.object().required(getFieldRequiredMessage()),
    items: Yup.array().of(
      Yup.object().shape({
        item: Yup.object().nullable().required(getFieldRequiredMessage()),
        quantity: Yup.number()
          .typeError(getQtyNum())
          .positive(getQtyPositive())
          .required(getFieldRequiredMessage()),
        modifiers: Yup.array().of(
          Yup.object().shape({
            modifier: Yup.array().of(Yup.object()).nullable(),
          })
        ),
      })
    ),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }
  return createSchemaShape(shape)
}

export const addModifierSchemas = mode => {
  let shape = {
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    nameEn: Yup.string().required(getFieldRequiredMessage()),
    item: Yup.string().required(getFieldRequiredMessage()),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .min(0, getMinimumNegMessage()),
    // type: Yup.string().required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    minimum: Yup.number().when("type", {
      is: val => val === "sizes",
      then: () =>
        Yup.number()
          .typeError(getOnlyNumMessage())
          .test("is-one", getMinExact1(), value => value === 1)
          .required(getFieldRequiredMessage()),
      otherwise: schema =>
        Yup.number()
          .typeError(getDisplayNumMessage())
          .required(getFieldRequiredMessage())
          .min(0, getMinimumNegMessage()),
    }),

    maximum: Yup.number().when("type", {
      is: val => val === "sizes",
      then: () =>
        Yup.number()
          .typeError(getOnlyNumMessage())
          .test("is-one", getMaxExact1(), value => value === 1)
          .required(getFieldRequiredMessage()),
      otherwise: schema =>
        Yup.number()
          .typeError(getDisplayNumMessage())
          .required(getFieldRequiredMessage())
          .min(Yup.ref("minimum"), getMaxMustGreaterMin()),
    }),

    // maxModifiersOptions: Yup.number().typeError(
    //   getDisplayNumMessage()
    // ),
    // freeModifiersCount: Yup.number().typeError(
    //   getDisplayNumMessage()
    // ),

    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "add" || mode === "allModifierAdd") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addModifierOptionsSchemas = mode => {
  let shape = {
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    nameEn: Yup.string().required(getFieldRequiredMessage()),
    caloriesForCalculations: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .min(0, getMinimumNegMessage()),
    // modifierGroup: Yup.object()
    //   .shape({
    //     label: Yup.string(),
    //     value: Yup.string().required(getFieldRequiredMessage()),
    //   })
    //   .nullable(),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .min(0, getMinimumNegMessage()),
    // minimum: Yup.number()
    //   .typeError(getOnlyNumMessage())
    //   .required(getFieldRequiredMessage())
    //   .min(0, getMinimumNegMessage()),
    // maximum: Yup.number()
    //   .typeError(getOnlyNumMessage())
    //   .min(Yup.ref("minimum"), getMaxMustGreaterMin())
    //   .required(getFieldRequiredMessage()),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    // modifierGroup: Yup.string().required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    // imageType: Yup.string().required(getFieldRequiredMessage()),
    // imageUrl: Yup.string().when("imageType", {
    //   is: val => val === "URL",
    //   then: () => Yup.mixed().required(getFieldRequiredMessage()),
    // }),
  }

  // if (mode === "add" || mode === "allModifierAdd") {
  //   shape = {
  //     ...shape,
  //     imageFile: Yup.string().when("imageType", {
  //       is: val => val === "UPLOAD",
  //       then: () => Yup.string().required(getFieldRequiredMessage()),
  //       otherwise: schema => schema.notRequired(),
  //     }),
  //   }
  // }
  if (mode === "allModifierAdd") {
    shape = {
      ...shape,
      modifierGroup: Yup.object()
        .shape({
          value: Yup.string().required(getFieldRequiredMessage()),
          label: Yup.string().required(getFieldRequiredMessage()),
        })
        .required(getFieldRequiredMessage()),
    }
  }

  if (mode === "edit" || mode === "allModifierEdit" || mode === "viewEdit") {
    shape = {
      ...shape,
      plu: Yup.string().optional().nullable(),
    }
  }

  return createSchemaShape(shape)
}

export const addItemGroupSchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    minimum: Yup.number()
      .typeError(getOnlyNumMessage())
      .required(getFieldRequiredMessage())
      .min(0, getFieldRequiredMessage()),
    maximum: Yup.number()
      .typeError(getOnlyNumMessage())
      .min(Yup.ref("minimum"), getMaxMustGreaterMin())
      .required(getFieldRequiredMessage()),
    quantity: Yup.number()
      .typeError(getQtyNum())
      .positive(getQtyPositive())
      .required(getFieldRequiredMessage()),
    items: Yup.array().min(1, getMinOneItemGroup()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
  }
  return createSchemaShape(shape)
}

export const itemAdvanceSettingSchema = () => {
  const shape = {
    category: Yup.string().required(getFieldRequiredMessage()),
    personCountPerServing: Yup.string().required(getFieldRequiredMessage()),
    caloriesForDisplay: Yup.string().required(getFieldRequiredMessage()),
    caloriesForCalculations: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const taxSchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    rate: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const itemVideoValidation = () => {
  const shape = {
    videoUrlType: Yup.string().required(getFieldRequiredMessage()),
    videoUrl: Yup.string().when("videoUrlType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
    }),
    video: Yup.string().when("videoUrlType", {
      is: val => val === "UPLOAD",
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const editCustomerSchema = () => {
  const shape = {
    firstName: Yup.string().required(getFieldRequiredMessage()),
    lastName: Yup.string().required(getFieldRequiredMessage()),
    // phoneCode: Yup.string().required(getFieldRequiredMessage()),
    // phoneNumber: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .matches(/^\d+$/, "Please enter a valid phone number"),
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        getValidEmail()
      )
      .required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addPromotionSchema = offerType => {
  var shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    offerType: Yup.string().required(getFieldRequiredMessage()),
    amount: Yup.number()
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .required(getFieldRequiredMessage()),

    // category: Yup.object().when("offerType", {
    //   is: val => val === "item",
    //   then: () => Yup.object().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),
    // item: Yup.object().when("offerType", {
    //   is: val => val === "item",
    //   then: () => Yup.object().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),
    // offerPrice: Yup.string().when("offerType", {
    //   is: val => val === "item",
    //   then: () => Yup.string().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),
    // quantity: Yup.string().when("offerType", {
    //   is: val => val === "item",
    //   then: () => Yup.string().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),

    // qualificationItems: Yup.string().when("offerType", {
    //   is: val => val === "category",
    //   then: () =>
    //     Yup.array().of(
    //       Yup.object().shape({
    //         category: Yup.object().required(getFieldRequiredMessage()),
    //         item: Yup.object().required(getFieldRequiredMessage()),
    //       })
    //     ),
    //   otherwise: schema => schema.notRequired(),
    // }),

    // qualificationItems: Yup.string().when("offerType", {
    //   is: val => val === "item",
    //   then: () =>
    //     Yup.array().of(
    //       Yup.object().shape({
    //         category: Yup.object().required(getFieldRequiredMessage()),
    //         item: Yup.object().required(getFieldRequiredMessage()),
    //       })
    //     ),
    //   otherwise: schema => schema.notRequired(),
    // }),

    // qualificationItems: Yup.array().of(
    //   Yup.object().shape({
    //     category: Yup.object().required(getFieldRequiredMessage()),
    //     item: Yup.object().required(getFieldRequiredMessage()),
    //   })
    // ),

    cartRules: Yup.array().of(
      Yup.object().shape({
        category: Yup.object().required(getFieldRequiredMessage()),
        item: Yup.object().required(getFieldRequiredMessage()),
        quantity: Yup.number()
          .typeError(getFieldRequiredMessage())
          .required(getFieldRequiredMessage())
          .positive(getQtyPositive()),
      })
    ),

    // quantity: Yup.string().when("offerType", {
    //   is: val => val === "item",
    //   then: () => Yup.string().required(getFieldRequiredMessage()),
    //   otherwise: schema => schema.notRequired(),
    // }),

    limits: Yup.object().shape({
      maxUsageOfCustomers: Yup.number().when("isMaxUsageOfCustomers", {
        is: val => val === true,
        then: () => Yup.number().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
      maxUsagePerCustomer: Yup.number().when("isMaxUsagePerCustomer", {
        is: val => val === true,
        then: () => Yup.number().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }),
  }
  if (offerType === "category") {
    shape = {
      ...shape,
      qualificationItems: Yup.array().of(
        Yup.object().shape({
          category: Yup.object().required(getFieldRequiredMessage()),
        })
      ),
    }
  }
  if (offerType === "item") {
    shape = {
      ...shape,
      qualificationItems: Yup.array().of(
        Yup.object().shape({
          category: Yup.object().required(getFieldRequiredMessage()),
          item: Yup.object().required(getFieldRequiredMessage()),
        })
      ),
    }
  }

  return createSchemaShape(shape)
}

export const addCouponGroupSchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
  }
  return createSchemaShape(shape)
}

export const addCouponSchema = mode => {
  let shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
    code: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[a-zA-Z0-9]+$/, getAlfaNumericChar())
      .trim(),
    promotion: Yup.string().required(getFieldRequiredMessage()),
    startsAt: Yup.string().required(getFieldRequiredMessage()),
    expiresAt: Yup.string()
      .required(getFieldRequiredMessage())
      .test("is-greater", getExpiredDateMsg(), function (value) {
        const { startsAt } = this.parent
        return new Date(value) > new Date(startsAt)
      }),
    // startDate: Yup.string().required(getFieldRequiredMessage()),
    // startTime: Yup.string().required(getFieldRequiredMessage()),
    // endDate: Yup.string().required(getFieldRequiredMessage()),
    // endTime: Yup.string().required(getFieldRequiredMessage()),
    promotion: Yup.object().required(getFieldRequiredMessage()),
    customers: Yup.array().when("isLimitToSpecificCustomer", {
      is: val => val === true,
      then: () =>
        Yup.array()
          .min(1, getMinOneCustomer())
          .required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "digitalAdd") {
    shape = {
      ...shape,
      imageUrl: Yup.string().when("imageType", {
        is: val => val === "URL",
        then: () => Yup.mixed().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addBulkCouponSchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    promotion: Yup.object().required(getFieldRequiredMessage()),
    couponGroup: Yup.object().required(getFieldRequiredMessage()),
    startsAt: Yup.string().required(getFieldRequiredMessage()),
    expiresAt: Yup.string()
      .required(getFieldRequiredMessage())
      .test("is-greater", getExpiredDateMsg(), function (value) {
        const { startsAt } = this.parent
        return new Date(value) > new Date(startsAt)
      }),
    // hasUpload: Yup.string().required(getFieldRequiredMessage()),
    file: Yup.string().when("hasUpload", {
      is: val => val === true,
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    prefix: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^[\da-zA-Z]+$/, getOnlyNumAndChar()),
      otherwise: schema => schema.notRequired(),
    }),
    noOfDigits: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^\d+$/, getOnlyNumMessage())
          .test("min-max-values", getValueBt3To10(), value => {
            const number = parseInt(value, 10)
            return number >= 3 && number <= 10
          }),

      otherwise: schema => schema.notRequired(),
    }),
    noOfCodes: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^[0-9]+$/, getOnlyNumMessage())
          .test("min-max-values", getValueBt1To1000(), value => {
            const number = parseInt(value, 10)
            return number >= 1 && number <= 1000
          }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const referralInfoSchema = () => {
  const shape = {
    earnedPointsForSender: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    earnedPointsForReceiver: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    pointValidityInDays: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const loyaltyPointInfoSchema = () => {
  const shape = {
    requiredPointsForOneSAR: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    minLimitOfPointRedemtion: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    pointValidityInDays: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    earningPointInMinutes: Yup.string()
      .min(0, getGreaterOrEqual0())
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    requiredCollectPointsForOneSAR: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    orderStatusForLoyaltyCredit: Yup.string().required(
      getFieldRequiredMessage()
    ),
  }

  return createSchemaShape(shape)
}

export const revokeLoyaltyPointsSchema = () => {
  const shape = {
    points: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .test(
        "is-greater-than-zero",
        getGreaterOrEqual0(),
        value => parseInt(value) > 0
      ),

    remarks: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const editNotificationSchema = () => {
  const shape = {
    titleEn: Yup.string().required(getFieldRequiredMessage()),
    titleAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    bodyEn: Yup.string().required(getFieldRequiredMessage()),
    bodyAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
  }

  return createSchemaShape(shape)
}

export const sendNotificationSchema = () => {
  const shape = {
    titleEn: Yup.string().required(getFieldRequiredMessage()),
    titleAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    bodyEn: Yup.string().required(getFieldRequiredMessage()),
    bodyAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),

    scheduledTime: Yup.string().when("isScheduled", {
      is: val => val === true,
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    customers: Yup.array().when("type", {
      is: val => val === "specific-customers",
      then: () =>
        Yup.array()
          .min(1, getMinOneCustomer())
          .required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const addCountrySchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    title: Yup.string().required(getFieldRequiredMessage()),
    titleAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    dialCode: Yup.string()
      .matches(/^\+/, getDialCodeStart())
      .required(getFieldRequiredMessage()),
    countryCode: Yup.string().required(getFieldRequiredMessage()),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    currencySymbol: Yup.string().required(getFieldRequiredMessage()),
    value: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .test("min-max-values", getValue0To100(), value => {
        const number = parseInt(value, 10)
        return number >= 1 && number <= 100
      }),
    registeredNo: Yup.string().required(getFieldRequiredMessage()),
    currency: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addRegionSchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    country: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addCitySchema = () => {
  const shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    country: Yup.object().required(getFieldRequiredMessage()),
    region: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addIssueSchema = () => {
  const shape = {
    title: Yup.string().required(getFieldRequiredMessage()),
    titleAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const updateOpeningHoursSchema = () => {
  const shape = {
    ids: Yup.array()
      .min(1, getMinOneLocation())
      .required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addIngredientSchema = mode => {
  let shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    // imageUrl: Yup.string().when("imageUrlType", {
    //   is: val => val === "URL",
    //   then: () => Yup.mixed().required(getFieldRequiredMessage()),
    // }),
  }

  // if (mode === "add") {
  //   shape = {
  //     ...shape,
  //     imageUrl: Yup.string().when("imageUrlType", {
  //       is: val => val === "URL",
  //       then: () => Yup.mixed().required(getFieldRequiredMessage()),
  //       otherwise: schema => schema.notRequired(),
  //     }),
  //     image: Yup.string().when("imageUrlType", {
  //       is: val => val === "UPLOAD",
  //       then: () => Yup.string().required(getFieldRequiredMessage()),
  //       otherwise: schema => schema.notRequired(),
  //     }),
  //   }
  // }

  return createSchemaShape(shape)
}

export const addAllergenSchema = mode => {
  let shape = {
    name: Yup.string().required(getFieldRequiredMessage()),
    nameAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),

    // imageUrl: Yup.string().when("imageUrlType", {
    //   is: val => val === "URL",
    //   then: () => Yup.mixed().required(getFieldRequiredMessage()),
    // }),
  }

  // if (mode === "add") {
  //   shape = {
  //     ...shape,
  //     imageUrl: Yup.string().when("imageUrlType", {
  //       is: val => val === "URL",
  //       then: () => Yup.mixed().required(getFieldRequiredMessage()),
  //       otherwise: schema => schema.notRequired(),
  //     }),
  //     image: Yup.string().when("imageUrlType", {
  //       is: val => val === "UPLOAD",
  //       then: () => Yup.string().required(getFieldRequiredMessage()),
  //       otherwise: schema => schema.notRequired(),
  //     }),
  //   }
  // }

  return createSchemaShape(shape)
}

export const addLoyaltyPointSchema = mode => {
  let shape = {
    points: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    remarks: Yup.string().required(getFieldRequiredMessage()),
  }
  if (mode === "GLOBAL") {
    shape = {
      ...shape,
      customers: Yup.array()
        .min(1, getMinOneCustomer())
        .required(getFieldRequiredMessage()),
    }
  }

  return createSchemaShape(shape)
}

export const copyBranchesSchema = () => {
  const shape = {
    ids: Yup.array()
      .min(1, getMinOneLocation())
      .required(getFieldRequiredMessage()),
    settings: Yup.array()
      .min(1, getMinOneSetting())
      .required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addFaqSchema = () => {
  const shape = {
    title: Yup.string().required(getFieldRequiredMessage()),
    titleAr: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    descriptionAr: Yup.string().matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    ),
  }

  return createSchemaShape(shape)
}

export const generateQrCodeSchema = () => {
  const shape = {
    branchId: Yup.object().required(getFieldRequiredMessage()),
    type: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addQuestionSchema = () => {
  const shape = {
    en: Yup.string().required(getFieldRequiredMessage()),
    ar: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
    type: Yup.string().required(getFieldRequiredMessage()),

    // scale: Yup.string().when("type", {
    //   is: val => val == "0",
    //   then: () =>
    //     Yup.string()
    //       .required(getFieldRequiredMessage())
    //       .matches(/^\d+$/, getValidNumMessage())
    //       .test(
    //         "min-max-values",
    //         "Value must be between 0 and 5",
    //         function (value) {
    //           const number = parseInt(value, 10)
    //           return number >= 0 && number <= 5
    //         }
    //       ),
    //   otherwise: () =>
    //     Yup.string()
    //       .required(getFieldRequiredMessage())
    //       .matches(/^\d+$/, getValidNumMessage())
    //       .test(
    //         "min-max-values",
    //         "Value must be between 0 and 10",
    //         function (value) {
    //           const number = parseInt(value, 10)
    //           return number >= 0 && number <= 10
    //         }
    //       ),
    // }),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const cloneBranchSchema = () => {
  const shape = {
    branchId: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addEmailsSchema = () => {
  const shape = {
    backgroundEmails: Yup.string()
      .required(getFieldRequiredMessage())
      .test("multiple-emails", getInvalidFormat(), function (value) {
        if (!value) return false

        // Split emails and trim whitespace
        const emails = value.split(",").map(email => email.trim())

        // Validate each email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emails.every(email => emailRegex.test(email))
      }),
  }

  return createSchemaShape(shape)
}

export const addVehicleSchema = mode => {
  let shape = {
    brand: Yup.string().required(getFieldRequiredMessage()),
  }

  if (mode === "add") {
    shape = {
      ...shape,
      image: Yup.string().required(getFieldRequiredMessage()),
    }
  }

  return createSchemaShape(shape)
}

export const funcName = () => {
  const shape = {}

  return createSchemaShape(shape)
}
